import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AgendaContexto,
  AgendaDisponibilidade,
  AgendaDisponibilidadeAgenda,
  AgendaDisponibilidadeUnidade,
  AgendaEstabelecimento,
  AgendaMedico,
  AgendaOpcao,
  AgendaPlanoPaciente,
  AgendaPrecoPaciente,
  AgendaPrecos,
  AgendaPreparoExame,
  AgendaUf,
  AgendaVaga,
} from '../../../../interfaces/agenda';
import { AgendaModaisService } from '../../../../services/agenda/agenda-modais.service';
import { AgendaService, mensagemErroAgenda } from '../../../../services/agenda/agenda.service';
import { areaAtuacaoDoTipo, codigoTipoAgenda, dataLocal, somarDias, tenantTemOdonto } from '../agenda-filtro';
import { mesAnterior, mesSeguinte, rotuloMes } from '../agenda-mes';
import { AgendaCascataExame, AgendaExameAgrupado, agruparExames, cascataExame, dataCurta } from '../agenda-transferir/agenda-transferir';
import { AgendaPacienteComponent } from '../agenda-paciente/agenda-paciente.component';
import { AgendaPacienteEscolhido } from '../agenda-paciente/agenda-paciente';
import {
  AgendaBuscaDisponibilidade,
  AgendaModoPreco,
  AgendaPrecoDoPaciente,
  chavePrecoPaciente,
  modoPreco,
  paramsPrecoPaciente,
  precoDoPaciente,
  AgendaCardAgenda,
  AgendaCardUnidade,
  AgendaCelulaCalendario,
  AgendaHorarioEscolhido,
  AgendaTipoBusca,
  DIAS_POR_ABA,
  HORARIOS_INICIAIS,
  JANELA_INICIAL_DIAS,
  MENSAGEM_LIMITADO,
  cardsDoDia,
  celulasCalendario,
  datasComVaga,
  faltaParaBuscar,
  fimHorizonte,
  horarioEscolhido,
  janelaSeguinte,
  mesclarDisponibilidade,
  paramsDisponibilidade,
  paramsPrecos,
  precoParticular,
  preparoDaAgenda,
  procInternoUnico,
  regraDeRetorno,
  rotuloIdade,
  tamanhoSeguinte,
  ultimoDiaDoMes,
} from './agenda-disponibilidade';

const ESPERA_EXAME_MS = 600;
const MIN_BUSCA_EXAME = 3;
const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Encontrar Disponibilidade: vagas da marca por unidade e médico a partir de uma data. Clicar no
 * horário pede ao pai o modal de novo agendamento; depois de gravar o pai chama aposAgendar.
 */
@Component({
  selector: 'app-agenda-disponibilidade',
  standalone: true,
  imports: [CommonModule, FormsModule, AgendaPacienteComponent],
  templateUrl: './agenda-disponibilidade.component.html',
  styleUrl: './agenda-disponibilidade.component.scss',
})
export class AgendaDisponibilidadeComponent implements OnInit, OnDestroy {
  @Input() contexto: AgendaContexto | null = null;
  /** Modal de agendamento aberto por cima: o Esc é dele. */
  @Input() coberto = false;

  @Output() fechar = new EventEmitter<void>();
  @Output() agendar = new EventEmitter<AgendaHorarioEscolhido>();

  readonly MENSAGEM_LIMITADO = MENSAGEM_LIMITADO;
  readonly HORARIOS_INICIAIS = HORARIOS_INICIAIS;
  readonly dataCurta = dataCurta;
  readonly rotuloIdade = rotuloIdade;
  readonly diasSemana = DIAS_SEMANA;

  // --- Filtros ---
  tipo: AgendaTipoBusca = 'CONSULTA';
  dtInicio = '';
  uf: string | null = null;
  cdEstabelecimento: number | null = null;
  cdEspecialidade: number | null = null;
  cdMedico: number | null = null;
  soRetorno = false;

  estabelecimentos: AgendaEstabelecimento[] = [];
  ufs: AgendaUf[] = [];
  especialidades: AgendaOpcao[] = [];
  medicos: AgendaMedico[] = [];
  carregandoMedicos = false;
  erroFiltros = '';

  // Paciente é opcional e não filtra vagas: só mostra o preço dele por unidade e vai para o agendamento.
  paciente: AgendaPacienteEscolhido | null = null;
  planoPaciente: AgendaPlanoPaciente | null = null;
  planoPronto = false;
  /** Por unidade + filtro + paciente/plano: rebuscar ou trocar de dia não repete a consulta. */
  precosPaciente: Record<string, AgendaPrecoDoPaciente> = {};
  /** Sem plano: particular por unidade, não depende do paciente. */
  precosParticular: Record<number, AgendaPrecoDoPaciente> = {};

  termoExame = '';
  examesEncontrados: AgendaExameAgrupado[] = [];
  buscandoExame = false;
  erroExame = '';
  exameEscolhido: AgendaExameAgrupado | null = null;
  cdMedicoExame: string | null = null;

  // --- Resultado ---
  resultado: AgendaDisponibilidade | null = null;
  busca: AgendaBuscaDisponibilidade | null = null;
  datas: string[] = [];
  offset = 0;
  dataSel: string | null = null;
  buscando = false;
  carregandoMais = false;
  buscou = false;
  erroBusca = '';
  sucesso = '';
  expandidos = new Set<string>();
  detalhes = new Set<string>();

  calendarioAberto = false;
  mesCal = '';

  /** Até onde o resultado cobre; null = horizonte esgotado. */
  private proximoInicio: string | null = null;
  private tamanho = JANELA_INICIAL_DIAS;
  private fim = '';
  private seqBusca = 0;
  private seqExame = 0;
  private seqMedicos = 0;
  private seqFiltros = 0;
  private timerExame: ReturnType<typeof setTimeout> | null = null;
  private fecharRegistro: (() => void) | null = null;

  constructor(private api: AgendaService, private modais: AgendaModaisService) {}

  async ngOnInit() {
    this.fecharRegistro = this.modais.abrir();
    this.dtInicio = this.hoje();
    await this.carregarFiltros();
  }

  ngOnDestroy() {
    this.fecharRegistro?.();
    this.fecharRegistro = null;
    if (this.timerExame) clearTimeout(this.timerExame);
    this.seqBusca++;
    this.seqExame++;
    this.seqMedicos++;
    this.seqFiltros++;
  }

  @HostListener('document:keydown.escape')
  aoEsc() {
    if (this.coberto || this.modais.coberto()) return;
    if (this.calendarioAberto) { this.calendarioAberto = false; return; }
    this.fechar.emit();
  }

  /** Relógio local; sobrescrito nos testes. */
  protected agora(): Date {
    return new Date();
  }

  private hoje(): string {
    return dataLocal(this.agora());
  }

  private horaAgora(): string {
    const d = this.agora();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  get supervisor(): boolean { return !!this.contexto?.supervisor; }
  get ehExame(): boolean { return this.tipo === 'EXAME'; }
  /** Só de consulta: o original não oferece o filtro de retorno na odonto. */
  get ehConsulta(): boolean { return this.tipo === 'CONSULTA'; }
  get temOdonto(): boolean { return tenantTemOdonto(this.contexto?.tenant); }

  // --- Cascata de filtros -------------------------------------------------------------------

  private async carregarFiltros() {
    const seq = ++this.seqFiltros;
    const tipo = codigoTipoAgenda(this.tipo);
    const areaAtuacao = areaAtuacaoDoTipo(this.tipo);
    this.erroFiltros = '';
    const [est, ufs, esp] = await Promise.allSettled([
      this.api.estabelecimentos({ tipo, areaAtuacao }),
      this.api.ufs(tipo, areaAtuacao),
      this.ehExame ? Promise.resolve([] as AgendaOpcao[]) : this.api.especialidades({ cdEstabelecimento: null, tipo, areaAtuacao }),
    ]);
    if (seq !== this.seqFiltros) return;
    this.estabelecimentos = est.status === 'fulfilled' ? est.value : [];
    this.ufs = ufs.status === 'fulfilled' ? ufs.value : [];
    this.especialidades = esp.status === 'fulfilled' ? esp.value : [];
    const falha = [est, ufs, esp].find(r => r.status === 'rejected') as PromiseRejectedResult | undefined;
    if (falha) this.erroFiltros = mensagemErroAgenda(falha.reason, 'Erro ao carregar os filtros.');
    if (this.uf && !this.ufs.some(u => u.uf === this.uf)) this.uf = null;
    if (this.cdEstabelecimento && !this.unidadesDaUf.some(e => e.cdEstabelecimento === this.cdEstabelecimento)) this.cdEstabelecimento = null;
    if (!this.ehExame) await this.carregarMedicos();
  }

  /** Unidades do tipo na UF escolhida (consulta); no exame vêm dos parceiros. */
  get unidadesDaUf(): AgendaEstabelecimento[] {
    return this.uf ? this.estabelecimentos.filter(e => e.uf === this.uf) : this.estabelecimentos;
  }

  async escolherTipo(t: AgendaTipoBusca) {
    if (this.tipo === t) return;
    this.tipo = t;
    this.cdEspecialidade = null;
    this.cdMedico = null;
    this.medicos = [];
    this.soRetorno = false;
    this.removerExame();
    this.limparResultado();
    await this.carregarFiltros();
  }

  async trocarUf(uf: string | null) {
    this.uf = uf || null;
    if (this.cdEstabelecimento && !this.ehExame && !this.unidadesDaUf.some(e => e.cdEstabelecimento === this.cdEstabelecimento)) {
      this.cdEstabelecimento = null;
    }
    if (this.ehExame) this.recalcularCascataExame();
    this.limparResultado();
    if (!this.ehExame) await this.carregarMedicos();
  }

  async trocarUnidade(cd: number | null) {
    this.cdEstabelecimento = cd;
    this.limparResultado();
    if (!this.ehExame) await this.carregarMedicos();
  }

  async trocarEspecialidade(cd: number | null) {
    this.cdEspecialidade = cd;
    this.limparResultado();
    await this.carregarMedicos();
  }

  trocarMedico(cd: number | null) {
    this.cdMedico = cd;
    this.limparResultado();
  }

  /** UF sem unidade: o back de médicos é por unidade, então junta as da UF (como o original). */
  private async carregarMedicos() {
    const seq = ++this.seqMedicos;
    this.carregandoMedicos = true;
    try {
      const unidades = this.cdEstabelecimento ? [this.cdEstabelecimento]
        : this.uf ? this.unidadesDaUf.map(e => e.cdEstabelecimento) : [null];
      const listas = await Promise.all(unidades.map(cd => this.api.medicos({ cdEstabelecimento: cd, cdEspecialidade: this.cdEspecialidade })));
      if (seq !== this.seqMedicos) return;
      const porCodigo = new Map<number, AgendaMedico>();
      listas.flat().forEach(m => { if (!porCodigo.has(m.cdMedico)) porCodigo.set(m.cdMedico, m); });
      this.medicos = [...porCodigo.values()].sort((a, b) => (a.nmMedico ?? '').localeCompare(b.nmMedico ?? ''));
      if (this.cdMedico && !porCodigo.has(this.cdMedico)) this.cdMedico = null;
    } catch {
      if (seq === this.seqMedicos) this.medicos = [];
    } finally {
      if (seq === this.seqMedicos) this.carregandoMedicos = false;
    }
  }

  // --- Exame --------------------------------------------------------------------------------

  digitarExame(q: string) {
    this.termoExame = q;
    if (this.timerExame) clearTimeout(this.timerExame);
    const seq = ++this.seqExame;
    if (q.trim().length < MIN_BUSCA_EXAME) {
      this.examesEncontrados = [];
      this.buscandoExame = false;
      this.erroExame = '';
      return;
    }
    this.timerExame = setTimeout(async () => {
      this.timerExame = null;
      this.buscandoExame = true;
      this.erroExame = '';
      try {
        const lista = await this.api.exames(q.trim());
        if (seq === this.seqExame) this.examesEncontrados = agruparExames(lista);
      } catch {
        if (seq === this.seqExame) { this.examesEncontrados = []; this.erroExame = 'Erro ao buscar exames.'; }
      } finally {
        if (seq === this.seqExame) this.buscandoExame = false;
      }
    }, ESPERA_EXAME_MS);
  }

  escolherExame(g: AgendaExameAgrupado) {
    this.exameEscolhido = g;
    this.examesEncontrados = [];
    this.termoExame = '';
    this.cdMedicoExame = null;
    this.cdEstabelecimento = null;
    this.uf = null;
    this.limparResultado();
  }

  removerExame() {
    this.exameEscolhido = null;
    this.cdMedicoExame = null;
    if (this.ehExame) this.cdEstabelecimento = null;
    this.limparResultado();
  }

  /** Parceiros do exame na UF escolhida (a UF vem da lista de unidades). */
  get parceirosNaUf() {
    const parceiros = this.exameEscolhido?.parceiros ?? [];
    if (!this.uf) return parceiros;
    const daUf = new Set(this.estabelecimentos.filter(e => e.uf === this.uf).map(e => e.cdEstabelecimento));
    return parceiros.filter(p => p.cdEstabelecimento !== null && daUf.has(p.cdEstabelecimento));
  }

  get cascata(): AgendaCascataExame {
    return cascataExame(this.parceirosNaUf, this.cdMedicoExame, this.cdEstabelecimento ? String(this.cdEstabelecimento) : null);
  }

  /** Parceiros que sobraram depois de médico e unidade: são as agendas da busca. */
  get parceirosFiltrados() {
    const c = this.cascata;
    return this.parceirosNaUf.filter(p => p.cdAgenda !== null && c.cdAgendas.includes(p.cdAgenda));
  }

  private recalcularCascataExame() {
    const c = this.cascata;
    this.cdMedicoExame = c.cdMedico;
    this.cdEstabelecimento = c.cdEstabelecimento ? Number(c.cdEstabelecimento) : null;
  }

  trocarMedicoExame(cd: string | null) {
    this.cdMedicoExame = cd || null;
    this.cdEstabelecimento = null;
    this.recalcularCascataExame();
    this.limparResultado();
  }

  trocarUnidadeExame(cd: string | null) {
    this.cdEstabelecimento = cd ? Number(cd) : null;
    this.recalcularCascataExame();
    this.limparResultado();
  }

  // --- Busca em janelas ---------------------------------------------------------------------

  montarBusca(): AgendaBuscaDisponibilidade {
    const exame = this.ehExame;
    const parceiros = exame ? this.parceirosFiltrados : [];
    return {
      tipo: this.tipo,
      dtInicio: this.dtInicio,
      cdEspecialidade: exame ? null : this.cdEspecialidade,
      cdMedico: exame ? null : this.cdMedico,
      uf: this.uf,
      cdEstabelecimento: this.cdEstabelecimento,
      soRetorno: this.ehConsulta && this.soRetorno,
      cdAgendas: [...new Set(parceiros.map(p => p.cdAgenda!))],
      nrSeqProcInterno: exame ? procInternoUnico(parceiros) : null,
      exameEscolhido: !!this.exameEscolhido,
    };
  }

  get falta(): string | null {
    return faltaParaBuscar(this.montarBusca());
  }

  limparFiltros() {
    this.uf = null;
    this.cdEstabelecimento = null;
    this.cdEspecialidade = null;
    this.cdMedico = null;
    this.soRetorno = false;
    this.dtInicio = this.hoje();
    this.removerExame();
    this.limparResultado();
    if (!this.ehExame) void this.carregarMedicos();
  }

  /** Filtro mudou: o que está na tela deixa de corresponder a ele. */
  limparResultado() {
    this.seqBusca++;
    this.resultado = null;
    this.busca = null;
    this.datas = [];
    this.offset = 0;
    this.dataSel = null;
    this.buscando = false;
    this.carregandoMais = false;
    this.buscou = false;
    this.erroBusca = '';
    this.precosParticular = {};
    this.expandidos.clear();
    this.detalhes.clear();
    this.calendarioAberto = false;
    this.proximoInicio = null;
  }

  async buscar() {
    if (this.buscando || this.falta) return;
    const busca = this.montarBusca();
    this.limparResultado();
    this.sucesso = '';
    await this.executar(busca, null, null);
  }

  /** Depois de agendar: refaz a mesma busca até onde já estava carregada, no mesmo dia. */
  async recarregar() {
    const busca = this.busca;
    if (!busca) return;
    const ate = this.ultimaDataCarregada();
    const dia = this.dataSel;
    this.limparResultado();
    await this.executar(busca, ate, dia);
  }

  async aposAgendar(mensagem: string) {
    this.sucesso = mensagem;
    await this.recarregar();
  }

  private ultimaDataCarregada(): string | null {
    return this.proximoInicio ? somarDias(this.proximoInicio, -1) : this.fim;
  }

  private async executar(busca: AgendaBuscaDisponibilidade, ate: string | null, dia: string | null) {
    const seq = this.seqBusca;
    this.busca = busca;
    this.fim = fimHorizonte(busca.dtInicio);
    this.proximoInicio = busca.dtInicio;
    this.tamanho = JANELA_INICIAL_DIAS;
    this.buscando = true;
    try {
      // Primeira página de abas cheia (ou o que houver no horizonte); na recarga, até onde estava.
      await this.carregarJanelas(seq, () => ate ? (this.proximoInicio ?? '9') <= ate : this.datas.length < DIAS_POR_ABA);
      if (seq !== this.seqBusca) return;
      this.buscou = true;
      const i = dia ? this.datas.indexOf(dia) : -1;
      this.dataSel = i >= 0 ? dia : this.datas[0] ?? null;
      this.offset = i >= 0 ? i - (i % DIAS_POR_ABA) : 0;
    } finally {
      if (seq === this.seqBusca) this.buscando = false;
    }
  }

  get podeCarregarMais(): boolean {
    return !!this.resultado && !this.resultado.limitado && this.proximoInicio !== null && !this.erroBusca;
  }

  /** Busca janela após janela enquanto `continuar` pedir; para no teto, no horizonte ou no erro. */
  private async carregarJanelas(seq: number, continuar: () => boolean) {
    while (seq === this.seqBusca && this.busca && continuar()) {
      const janela = janelaSeguinte(this.proximoInicio, this.tamanho, this.fim);
      if (!janela) { this.proximoInicio = null; return; }
      let r: AgendaDisponibilidade;
      try {
        r = await this.api.disponibilidade(paramsDisponibilidade(this.busca, janela, this.supervisor));
      } catch (e) {
        if (seq === this.seqBusca) this.erroBusca = mensagemErroAgenda(e, 'Erro ao buscar vagas.');
        return;
      }
      if (seq !== this.seqBusca) return;
      if (this.busca.soRetorno && this.supervisor) {
        if (!r.retornosVerificados) { this.erroBusca = 'Não foi possível verificar as vagas de retorno.'; return; }
        r = regraDeRetorno(r);
      }
      this.resultado = mesclarDisponibilidade(this.resultado, r);
      this.datas = datasComVaga(this.resultado, this.hoje(), this.horaAgora());
      const seguinte = somarDias(janela.dtFim, 1);
      this.proximoInicio = seguinte > this.fim ? null : seguinte;
      this.tamanho = tamanhoSeguinte(this.tamanho, !r.datas.length);
      void this.carregarPrecosParticular(seq);
      void this.carregarPrecosPaciente();
      if (r.limitado) return;
    }
  }

  private async carregarMais(continuar: () => boolean) {
    if (!this.podeCarregarMais || this.carregandoMais) return;
    const seq = this.seqBusca;
    this.carregandoMais = true;
    try {
      await this.carregarJanelas(seq, () => this.proximoInicio !== null && continuar());
    } finally {
      if (seq === this.seqBusca) this.carregandoMais = false;
    }
  }

  // --- Abas de 3 dias e calendário ----------------------------------------------------------

  get abas(): string[] {
    return this.datas.slice(this.offset, this.offset + DIAS_POR_ABA);
  }

  get temAnterior(): boolean { return this.offset > 0; }
  get temSeguinte(): boolean { return this.offset + DIAS_POR_ABA < this.datas.length || this.podeCarregarMais; }

  rotuloAba(d: string): string {
    if (d === this.hoje()) return 'hoje';
    const [a, m, dd] = d.split('-').map(Number);
    return DIAS_SEMANA[new Date(a, m - 1, dd).getDay()];
  }

  escolherData(d: string) {
    this.dataSel = d;
  }

  abasAnteriores() {
    if (!this.temAnterior) return;
    this.offset = Math.max(0, this.offset - DIAS_POR_ABA);
    this.dataSel = this.datas[this.offset] ?? null;
  }

  /** Avança; no fim do que já veio, busca a janela seguinte até ter dias para mostrar. */
  async abasSeguintes() {
    const alvo = this.offset + DIAS_POR_ABA;
    if (alvo >= this.datas.length) await this.carregarMais(() => this.datas.length <= alvo);
    if (alvo < this.datas.length) {
      this.offset = alvo;
      this.dataSel = this.datas[alvo];
    }
  }

  alternarCalendario() {
    this.calendarioAberto = !this.calendarioAberto;
    if (this.calendarioAberto) this.mesCal = (this.dataSel ?? this.busca?.dtInicio ?? this.hoje()).slice(0, 7);
  }

  get rotuloMesCal(): string { return rotuloMes(this.mesCal); }

  get celulasCal(): AgendaCelulaCalendario[] {
    return celulasCalendario(this.mesCal, new Set(this.datas), this.hoje());
  }

  get podeMesAnterior(): boolean {
    return !!this.busca && this.mesCal > this.busca.dtInicio.slice(0, 7);
  }

  get podeMesSeguinte(): boolean {
    return !!this.busca && this.mesCal < this.fim.slice(0, 7);
  }

  /** Mês ainda não carregado aparece sem dias marcados até as janelas dele chegarem. */
  get mesCalPendente(): boolean {
    const ultima = this.ultimaDataCarregada();
    return this.podeCarregarMais && !!ultima && ultima < ultimoDiaDoMes(this.mesCal);
  }

  mesCalAnterior() {
    if (this.podeMesAnterior) this.mesCal = mesAnterior(this.mesCal);
  }

  async mesCalSeguinte() {
    if (!this.podeMesSeguinte) return;
    this.mesCal = mesSeguinte(this.mesCal);
    const fimMes = ultimoDiaDoMes(this.mesCal);
    await this.carregarMais(() => (this.proximoInicio ?? '9') <= fimMes);
  }

  escolherDiaCalendario(c: AgendaCelulaCalendario) {
    if (!c.data || !c.comVaga) return;
    const i = this.datas.indexOf(c.data);
    if (i < 0) return;
    this.dataSel = c.data;
    this.offset = i - (i % DIAS_POR_ABA);
    this.calendarioAberto = false;
  }

  // --- Cards --------------------------------------------------------------------------------

  get cards(): AgendaCardUnidade[] {
    return cardsDoDia(this.resultado, this.dataSel, this.hoje(), this.horaAgora());
  }

  totalDoDia(cards: AgendaCardUnidade[]): number {
    return cards.reduce((n, c) => n + c.total, 0);
  }

  chaveCard(u: AgendaDisponibilidadeUnidade, a: AgendaDisponibilidadeAgenda): string {
    return `${u.cdEstabelecimento}_${a.cdAgenda}`;
  }

  horariosVisiveis(u: AgendaDisponibilidadeUnidade, c: AgendaCardAgenda): AgendaVaga[] {
    return this.expandidos.has(this.chaveCard(u, c.agenda)) ? c.horarios : c.horarios.slice(0, HORARIOS_INICIAIS);
  }

  alternarMais(u: AgendaDisponibilidadeUnidade, a: AgendaDisponibilidadeAgenda) {
    const k = this.chaveCard(u, a);
    if (this.expandidos.has(k)) this.expandidos.delete(k);
    else this.expandidos.add(k);
  }

  alternarDetalhe(k: string) {
    if (this.detalhes.has(k)) this.detalhes.delete(k);
    else this.detalhes.add(k);
  }

  preparo(a: AgendaDisponibilidadeAgenda): AgendaPreparoExame | null {
    if (this.busca?.tipo !== 'EXAME') return null;
    return preparoDaAgenda(a.cdAgenda, this.exameEscolhido?.parceiros ?? [], this.resultado?.preparo ?? null);
  }

  get semCotacao(): boolean {
    return !!this.busca && this.busca.tipo !== 'EXAME' && !this.busca.cdEspecialidade;
  }

  get modoPreco(): AgendaModoPreco {
    return modoPreco(!!this.paciente, this.planoPronto, this.planoPaciente);
  }

  /** Só aparece preço com paciente escolhido (com plano ou particular). */
  get mostraPreco(): boolean {
    return this.modoPreco === 'paciente' || this.modoPreco === 'particular';
  }

  aoMudarPaciente(p: AgendaPacienteEscolhido | null) {
    this.paciente = p;
    this.planoPaciente = null;
    this.planoPronto = false;
  }

  aoCarregarPlano(plano: AgendaPlanoPaciente | null) {
    this.planoPaciente = plano;
    this.planoPronto = true;
    void this.carregarPrecosParticular(this.seqBusca);
    void this.carregarPrecosPaciente();
  }

  private paramsPrecoPaciente(cd: number) {
    if (!this.busca || !this.paciente || !this.planoPaciente) return null;
    return paramsPrecoPaciente(this.busca, cd, this.resultado, this.exameEscolhido?.parceiros ?? [], this.paciente.cdPessoaFisica, this.planoPaciente);
  }

  precoPacienteDa(cd: number): AgendaPrecoDoPaciente | null {
    const params = this.paramsPrecoPaciente(cd);
    return params ? this.precosPaciente[chavePrecoPaciente(params)] ?? null : null;
  }

  precoParticularDa(cd: number): AgendaPrecoDoPaciente | null {
    return this.precosParticular[cd] ?? null;
  }

  /** Linha de preço do card: a do plano, a particular, ou nenhuma sem paciente. */
  precoLinhaDa(cd: number): AgendaPrecoDoPaciente | null {
    if (this.modoPreco === 'paciente') return this.precoPacienteDa(cd);
    if (this.modoPreco === 'particular') return this.precoParticularDa(cd);
    return null;
  }

  /** Erro fica na tela, mas não no cache: a próxima carga tenta de novo. */
  private async carregarPrecosPaciente() {
    if (this.modoPreco !== 'paciente' || !this.resultado) return;
    const plano = this.planoPaciente!;
    await Promise.all(this.resultado.unidades.map(async u => {
      const params = this.paramsPrecoPaciente(u.cdEstabelecimento);
      if (!params) return;
      const chave = chavePrecoPaciente(params);
      const atual = this.precosPaciente[chave];
      if (atual && atual.estado !== 'erro') return;
      this.precosPaciente[chave] = { estado: 'carregando' };
      let p: AgendaPrecoPaciente | null = null;
      try {
        p = await this.api.precoPaciente(params);
      } catch {
        p = null;
      }
      this.precosPaciente[chave] = precoDoPaciente(p, plano);
    }));
  }

  /** Paciente sem plano: só o particular, uma consulta por unidade nova. */
  private async carregarPrecosParticular(seq: number) {
    const busca = this.busca;
    if (!busca || !this.resultado || this.modoPreco !== 'particular') return;
    const parceiros = this.exameEscolhido?.parceiros ?? [];
    const novas = this.resultado.unidades.map(u => u.cdEstabelecimento).filter(cd => !(cd in this.precosParticular));
    await Promise.all(novas.map(async cd => {
      const params = paramsPrecos(busca, cd, this.resultado, parceiros);
      if (!params) return;
      this.precosParticular[cd] = { estado: 'carregando' };
      let p: AgendaPrecos | null = null;
      try {
        p = await this.api.precos(params);
      } catch {
        p = null;
      }
      if (seq === this.seqBusca) this.precosParticular[cd] = precoParticular(p);
    }));
  }

  clicarHorario(u: AgendaDisponibilidadeUnidade, a: AgendaDisponibilidadeAgenda, h: AgendaVaga) {
    if (!this.busca) return;
    this.agendar.emit({
      ...horarioEscolhido(h, u, a, this.busca, this.exameEscolhido?.nome ?? '', this.exameEscolhido?.parceiros ?? [], this.resultado),
      paciente: this.paciente,
    });
  }

  brl(v: number): string { return BRL.format(v); }

  trackUnidade(_: number, c: AgendaCardUnidade) { return c.unidade.cdEstabelecimento; }
  trackAgenda(_: number, c: AgendaCardAgenda) { return c.agenda.cdAgenda; }
  trackVaga(_: number, v: AgendaVaga) { return v.id; }
}
